import styled from "@emotion/styled";
import { NATIVE_SOL } from "@stepfinance/common";
import type { StepColumnsType } from "@stepfinance/react";
import { formatBalance, IconTextContainer, StepTable, transparentWhite } from "@stepfinance/react";
import type { ParsedTransaction } from "@stepfinance/transaction-history";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import shallow from "zustand/shallow";

import { ReactComponent as Minus } from "../../assets/minusIcon.svg";
import { ReactComponent as Plus } from "../../assets/plusIcon.svg";
import { useLanguageContext } from "../../contexts/language/language";
import { useNavigationContext } from "../../contexts/navigation";
import { useLatestTransactionHistory } from "../../hooks/queries/transactionHistory/useLatestTransactionHistory";
import { useTransactionHistoryStore } from "../../hooks/stores/transactionHistoryStore";
import { getTokenByMint } from "../../utils/tokenInfo";
import { walletIsFeePayer } from "../../utils/transaction-history/utils";
import { NavigationLink } from "../shared";
import { UnboughtTier } from "../TiersModal/UnboughtTier";
import { TokenIcon } from "../TokenIcon";
import { PlatformCell } from "../tools/OpenOrders/PlatformCell";
import type { TransactionHistoryIconTypes } from "./Icons";
import { getTransactionHistoryIconComponent } from "./Icons";

//MOVE to COMMON
export const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  year: "numeric",
});

const MAX_TRANSACTION_COUNT = 100;

export const LatestUserTransactions: FC = () => {
  // const [page, setPage] = useState(0);
  const {
    data: latestTransactionData,
    isLoading,
    isSuccess,
    // isError,
  } = useLatestTransactionHistory();
  const setTiersModalOpen = useTransactionHistoryStore((state) => state.setTiersModalOpen, shallow);

  const [hasAllTransactionsLoaded, setHasAllTransactionsLoaded] = useState(false);
  const [isUnboughtTierShown, setIsUnboughtTierShown] = useState(false);
  const {
    currentLanguageData: {
      pages: { transactionHistory },
    },
    languageSetting,
  } = useLanguageContext();
  const { navigate } = useNavigationContext();

  useEffect(() => {
    if (isSuccess && latestTransactionData && latestTransactionData.length < MAX_TRANSACTION_COUNT) {
      setHasAllTransactionsLoaded(true);
    }
  }, [isSuccess, latestTransactionData]);

  const onReachScrollEnd = () => {
    setHasAllTransactionsLoaded(true);
    setIsUnboughtTierShown(true);
  };

  const columns: StepColumnsType<ParsedTransaction> = [
    // patch, this column \shouldn't be here
    {
      className: "column-custom",
      shouldDisplayRow: () => false,
      onRender: undefined,
    },
    {
      title: "Date",
      key: "txDateTime",
      width: 190,
      className: "column-string",
      onRender: (_, record) => <>{DATE_FORMATTER.format(new Date(record.txDateTime))}</>,
      sorter: undefined,
    },
    {
      title: "TXID",
      key: "txId",
      width: 80,
      onRender: (_, record) => {
        return (
          <NavigationLink href={`https://solscan.io/tx/${record.txSignature}`} target="_blank">
            <ShortSpan>{record.txSignature}</ShortSpan>
          </NavigationLink>
        );
      },
    },
    {
      title: "Platforms",
      key: "platform",
      width: 250,
      onRender: (_, record) => {
        if (!record.metadata.platform.length) {
          return <ZeroStateSpan>{transactionHistory.transactionTypes.unknown}</ZeroStateSpan>;
        }
        return (
          <>
            {record.metadata.platform.map((ele, idx) => (
              <PlatformCell platform={ele} key={idx} />
            ))}
          </>
        );
      },
      filters: undefined,
      filterDropdown: undefined,
      onFilter: undefined,
    },
    {
      title: "Type",
      key: "action",
      width: 150,
      className: "column-string",
      onRender: (_, record) => {
        if (!record.metadata.action.length) {
          return <ZeroStateSpan>{transactionHistory.transactionTypes.unknown}</ZeroStateSpan>;
        }

        return (
          <>
            {record.metadata.action.map((ele, indx) => {
              const IconComponent: FC = getTransactionHistoryIconComponent(ele as TransactionHistoryIconTypes);
              return (
                <IconComponentContainer key={indx}>
                  <IconComponent />
                </IconComponentContainer>
              );
            })}
          </>
        );
      },
      filters: undefined,
      filterDropdown: undefined,
      onFilter: undefined,
    },

    {
      title: "Outgoing",
      key: "outgoing",
      onRender: (_, record) => {
        const sentData = Object.entries(record.metadata.parsedBalanceChanges.sent);

        if (walletIsFeePayer(record)) {
          // Add paid tx fee as a separate row
          sentData.push([NATIVE_SOL.toBase58(), record.txFee]);
        }

        if (!sentData.length) {
          return <ZeroStateSpan>--</ZeroStateSpan>;
        }

        return (
          <>
            {sentData.map(([mint, value], indx) => {
              const token = getTokenByMint(mint);
              const symbolOrName = token?.symbol && token?.symbol.length > 0 ? token?.symbol : token?.name;
              const description = symbolOrName ? (
                <MediumSpan> {symbolOrName} </MediumSpan>
              ) : (
                <ShortSpan>{mint}</ShortSpan>
              );
              return (
                <TokenWrapper key={indx} indx={indx}>
                  <Minus />
                  <TokenIcon mintAddress={mint} />
                  <span>{formatBalance(value, token?.decimals)}</span>
                  <NavigationLink
                    href={`https://analytics.step.finance/defionsolana/token?mint=${mint}`}
                    target="_blank"
                  >
                    {description}
                  </NavigationLink>
                </TokenWrapper>
              );
            })}
          </>
        );
      },
    },
    {
      title: "Incoming",
      key: "incoming",
      className: "column-string",
      onRender: (_, record) => {
        const receivedData = Object.entries(record.metadata.parsedBalanceChanges.recieved);
        if (!receivedData.length) {
          return <ZeroStateSpan>--</ZeroStateSpan>;
        }
        return (
          <>
            {receivedData.map(([mint, value], indx) => {
              const token = getTokenByMint(mint);
              const symbolOrName = token?.symbol && token?.symbol.length > 0 ? token?.symbol : token?.name;
              const description = symbolOrName ? (
                <MediumSpan> {symbolOrName} </MediumSpan>
              ) : (
                <ShortSpan>{mint}</ShortSpan>
              );
              return (
                <TokenWrapper key={indx} indx={indx}>
                  <Plus />
                  <TokenIcon mintAddress={mint} />
                  <span>{formatBalance(value, token?.decimals)}</span>
                  <NavigationLink
                    href={`https://analytics.step.finance/defionsolana/token?mint=${mint}`}
                    target="_blank"
                  >
                    {description}
                  </NavigationLink>
                </TokenWrapper>
              );
            })}
          </>
        );
      },
    },
  ];

  return (
    <>
      <StepTable
        rowKey={(record) => record.txSignature}
        columns={columns}
        dataSource={isSuccess ? latestTransactionData : []}
        loading={isLoading}
        onReachScrollEnd={onReachScrollEnd}
        finishedLoadingMessage={
          <>
            {transactionHistory.viewingFreeTransactions}
            <Link style={{ marginLeft: 4 }} to={"/" + languageSetting + "/history/calendar"}>
              {transactionHistory.visitTheCalendarPage}
            </Link>
            ,
          </>
        }
        hasFinishedLoadingAllItems={hasAllTransactionsLoaded}
        infiniteScroll
        scroll={{ y: "100vh", x: 190 + 80 + 200 + 150 + 500 }}
      />
      <UnboughtTier
        onClose={() => {
          setIsUnboughtTierShown(false);
        }}
        onSuccess={() => {
          setIsUnboughtTierShown(false);
          navigate("/history/calendar");
          setTiersModalOpen(true);
        }}
        visible={isUnboughtTierShown}
        width={500}
      />
    </>
  );
};

const IconComponentContainer = styled(IconTextContainer)`
  svg {
    path {
      fill: ${transparentWhite};
    }
  }
`;

const ShortSpan = styled.span`
  text-overflow: ellipsis;
  width: 45px;
  overflow: hidden;
`;

const MediumSpan = styled.span`
  text-overflow: ellipsis;
  max-width: 120px;
  overflow: hidden;
`;

const ZeroStateSpan = styled.span`
  color: #7d7d7d;
`;

type TokenWrapperProps = {
  indx: number;
};
const TokenWrapper = styled.div((props: TokenWrapperProps) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  rowGap: 16,
  paddingTop: props.indx === 0 ? 0 : 16,
}));
